/**
 * HyperDX error reporting - only report runtime/unexpected errors.
 * Do NOT report: API 4xx, validation errors, auth errors, code-thrown business errors.
 * DO report: network failures, uncaught exceptions, load failures, 5xx-style errors.
 */

/** Error-like object that may have HTTP status (e.g. from API client) */
interface ErrorWithStatus extends Error {
  status?: number;
  statusCode?: number;
  response?: { status?: number };
}

/**
 * Returns true if the error should be sent to HyperDX (runtime/unexpected).
 * @param error - Caught error (Error or object with message/status)
 * @param context - Optional context that may include status from API response
 */
export function isRuntimeError(
  error: unknown,
  context?: Record<string, string | number | boolean>
): boolean {
  if (error == null) return false;

  const err = error as ErrorWithStatus;
  const msg = (err?.message || String(error)).toLowerCase();
  const name = (err?.name || '').toLowerCase();

  // User cancelled / abort - do not report
  if (name === 'aborterror' || msg.includes('abort')) return false;

  // Explicit 4xx from API or context - do not report
  const status =
    err?.status ?? err?.statusCode ?? err?.response?.status ?? (context?.status as number);
  if (typeof status === 'number' && status >= 400 && status < 500) return false;

  // Common expected messages - do not report
  const expectedPatterns = [
    'unauthorized',
    'forbidden',
    'not found',
    'validation',
    'invalid',
    'already taken',
    'already registered',
    'wrong password',
    'user-not-found',
    'email not verified',
  ];
  if (expectedPatterns.some((p) => msg.includes(p))) return false;

  // Network / load / runtime - report
  if (err instanceof TypeError && (msg.includes('fetch') || msg.includes('network')))
    return true;
  if (
    msg.includes('failed to fetch') ||
    msg.includes('network error') ||
    msg.includes('load failed') ||
    msg.includes('timeout') ||
    msg.includes('chunk') ||
    msg.includes('loading')
  )
    return true;

  // 5xx from API - report
  if (typeof status === 'number' && status >= 500) return true;

  // Uncaught React/JS errors (no status, not in expected list) - treat as runtime
  if (err instanceof Error && status == null && !expectedPatterns.some((p) => msg.includes(p)))
    return true;

  return false;
}

/** Patterns that indicate validation / 4xx-style messages (for global error listener when status isn't on the error) */
const VALIDATION_MESSAGE_PATTERNS = [
  'unauthorized',
  'forbidden',
  'not found',
  'validation',
  'invalid',
  'already taken',
  'already registered',
  'wrong password',
  'user-not-found',
  'email not verified',
  'required',
  'must contain',
  'format',
  'minimum',
  'maximum',
  'length',
  'is required',
];

/**
 * Returns true if the message looks like a validation/400-style error (so we don't send to HyperDX).
 * Used by the global error listener for uncaught errors that may not have status on the error object.
 */
export function isLikelyValidationOr400Message(message: string | undefined): boolean {
  if (!message || typeof message !== 'string') return false;
  const msg = message.toLowerCase();
  return VALIDATION_MESSAGE_PATTERNS.some((p) => msg.includes(p));
}
